import { SummaryCard } from '../../../types';

export const matematikCards: SummaryCard[] = [
  // MATEMATİK DERSİ - 12 KONU
  {
    id: 'mt-001',
    title: 'Çarpanlar ve Katlar',
    subject: 'matematik',
    topic: 'Sayılar ve İşlemler',
    content: `# Çarpanlar ve Katlar

## Çarpan Nedir?
Bir sayıyı tam olarak bölen sayıya **çarpan** denir.
**Örnek**: 12 sayısının çarpanları: 1, 2, 3, 4, 6, 12

## Kat Nedir?
Bir sayının pozitif tam sayılarla çarpımlarına **kat** denir.
**Örnek**: 3'ün katları: 3, 6, 9, 12, 15, 18, 21...

## En Büyük Ortak Bölen (EBOB)
İki veya daha fazla sayının ortak çarpanlarının en büyüğü.
**Hesaplama Yöntemi**: Sayıları asal çarpanlarına ayırıp ortak çarpanların en küçük kuvvetlerini çarp.

## En Küçük Ortak Kat (EKOK)
İki veya daha fazla sayının ortak katlarının en küçüğü.
**Hesaplama Yöntemi**: Sayıları asal çarpanlarına ayırıp tüm çarpanların en büyük kuvvetlerini çarp.

## Asal Sayılar
1 ve kendisinden başka hiçbir pozitif böleni olmayan, 1'den büyük sayılardır.
**İlk Asal Sayılar**: 2, 3, 5, 7, 11, 13, 17, 19, 23...`,
    keyPoints: [
      'Bir sayının çarpanları sayıyı tam bölen sayılardır',
      'Bir sayının katları o sayının pozitif tam sayılarla çarpımlarıdır',
      'EBOB, ortak çarpanların en büyüğüdür',
      'EKOK, ortak katların en küçüğüdür',
      'Asal sayılar yalnızca 1 ve kendisiyle bölünebilen sayılardır'
    ],
    formulas: [
      'EBOB(a, b) × EKOK(a, b) = a × b',
      'İki ardışık sayının EBOB\'u = 1',
      'Aralarında asal iki sayının EKOK\'u = a × b'
    ],
    examples: [
      'EBOB(12, 18) = 6',
      'EKOK(4, 6) = 12',
      '15 = 3 × 5 (asal çarpanları)',
      '24\'ün çarpanları: 1, 2, 3, 4, 6, 8, 12, 24'
    ],
    difficulty: 'medium',
    estimatedReadTime: 8,
    tags: ['çarpan', 'kat', 'ebob', 'ekok', 'asal sayı'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-002',
    title: 'Üslü İfadeler',
    subject: 'matematik',
    topic: 'Cebirsel İfadeler',
    content: `# Üslü İfadeler

## Üs Nedir?
Bir sayının kendisiyle kaç kez çarpıldığını gösteren sayıdır.
**Örnek**: 2⁴ = 2 × 2 × 2 × 2 = 16

## Üslü İfadelerin Özellikleri
### Aynı Tabanlı Üslerin Çarpımı
aᵐ × aⁿ = aᵐ⁺ⁿ

### Aynı Tabanlı Üslerin Bölümü
aᵐ ÷ aⁿ = aᵐ⁻ⁿ (a ≠ 0)

### Üssün Üssü
(aᵐ)ⁿ = aᵐˣⁿ

### Çarpımın Üssü
(a × b)ⁿ = aⁿ × bⁿ

### Bölümün Üssü
(a ÷ b)ⁿ = aⁿ ÷ bⁿ (b ≠ 0)

## Özel Durumlar
- a⁰ = 1 (a ≠ 0)
- a¹ = a
- a⁻ⁿ = 1/aⁿ (a ≠ 0)`,
    keyPoints: [
      'Üs, tabanın kendisiyle kaç kez çarpıldığını gösterir',
      'Aynı tabanlı üsler çarpılırken üsler toplanır',
      'Aynı tabanlı üsler bölünürken üsler çıkarılır',
      'Herhangi bir sayının sıfırıncı kuvveti 1\'dir',
      'Negatif üsler, pozitif üssün tersini verir'
    ],
    formulas: [
      'aᵐ × aⁿ = aᵐ⁺ⁿ',
      'aᵐ ÷ aⁿ = aᵐ⁻ⁿ',
      '(aᵐ)ⁿ = aᵐˣⁿ',
      '(a × b)ⁿ = aⁿ × bⁿ',
      'a⁻ⁿ = 1/aⁿ'
    ],
    examples: [
      '3⁴ = 3 × 3 × 3 × 3 = 81',
      '2⁵ × 2³ = 2⁸ = 256',
      '5⁶ ÷ 5² = 5⁴ = 625',
      '(2³)² = 2⁶ = 64',
      '4⁻² = 1/4² = 1/16'
    ],
    difficulty: 'medium',
    estimatedReadTime: 7,
    tags: ['üs', 'üslü ifade', 'taban', 'kuvvet', 'çarpma'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-003',
    title: 'Kareköklü İfadeler',
    subject: 'matematik',
    topic: 'Kökler ve Üsler',
    content: `# Kareköklü İfadeler

## Karekök Nedir?
Karesi verilen sayıya eşit olan sayıya **karekök** denir.
**Örnek**: √16 = 4 (çünkü 4² = 16)

## Karekök Özellikleri
### Çarpım Kuralı
√a × √b = √(a × b) (a ≥ 0, b ≥ 0)

### Bölüm Kuralı
√a ÷ √b = √(a ÷ b) (a ≥ 0, b > 0)

### Karekökte Çıkarma
√(a²) = |a| = a (a ≥ 0)

## Karekök Sadeleştirme
Karekök altındaki sayıyı tam kare çarpanlarına ayırarak sadeleştiririz.
**Örnek**: √72 = √(36 × 2) = 6√2

## Karekök Hesaplama Yöntemleri
1. **Tam kareler**: √1, √4, √9, √16, √25...
2. **Çarpanlara ayırma**: √50 = √(25 × 2) = 5√2
3. **Rasyonelleştirme**: 1/√2 = √2/2

## Rasyonelleştirme
Payda veya paydada bulunan karekökten kurtulma işlemidir.`,
    keyPoints: [
      'Karekök, karesi verilen sayıya eşit olan sayıdır',
      'Negatif sayıların gerçel sayılarda karekökü yoktur',
      'Çarpım ve bölüm kuralları karekök işlemlerini kolaylaştırır',
      'Karekök sadeleştirmede tam kare çarpanlar çıkarılır',
      'Rasyonelleştirme ile paydadaki köklerden kurtuluruz'
    ],
    formulas: [
      '√a × √b = √(a × b)',
      '√a ÷ √b = √(a ÷ b)',
      '√(a²) = |a|',
      '(√a)² = a (a ≥ 0)',
      '1/√a = √a/a'
    ],
    examples: [
      '√36 = 6',
      '√12 = √(4 × 3) = 2√3',
      '√2 × √8 = √16 = 4',
      '√18 ÷ √2 = √9 = 3',
      '3/√5 = 3√5/5'
    ],
    difficulty: 'medium',
    estimatedReadTime: 6,
    tags: ['karekök', 'kök', 'sadeleştirme', 'rasyonelleştirme'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-004',
    title: 'Veri Analizi',
    subject: 'matematik',
    topic: 'İstatistik',
    content: `# Veri Analizi

## Veri Türleri
### Nitel Veriler
Sayısal olmayan, özellik belirten verilerdir.
**Örnekler**: Cinsiyet, renk, meslek, marka

### Nicel Veriler
Sayısal olan, ölçülebilen verilerdir.
**Örnekler**: Boy, kilo, yaş, sıcaklık

## Merkezi Eğilim Ölçüleri
### Aritmetik Ortalama (Ortalama)
Tüm değerlerin toplamının veri sayısına bölümü.
**Formül**: x̄ = (x₁ + x₂ + ... + xₙ) ÷ n

### Medyan (Ortanca)
Verileri küçükten büyüğe sıraladığımızda ortada kalan değer.

### Mod (Tepe Değer)
En sık tekrar eden değer.

## Yaygınlık Ölçüleri
### Değişim Aralığı (Ranj)
En büyük değer ile en küçük değer arasındaki fark.

## Veri Toplama ve Düzenleme
- **Anket**: Sorular aracılığıyla veri toplama
- **Gözlem**: Doğrudan gözlemleyerek veri toplama
- **Sıklık Tablosu**: Verilerin tekrar sayılarını gösteren tablo`,
    keyPoints: [
      'Veriler nitel ve nicel olarak ikiye ayrılır',
      'Aritmetik ortalama, tüm değerlerin ortalamasıdır',
      'Medyan, sıralı verilerin ortanca değeridir',
      'Mod, en sık tekrar eden değerdir',
      'Değişim aralığı, verilerin yayılımını gösterir'
    ],
    formulas: [
      'Aritmetik Ortalama = (Σx) ÷ n',
      'Değişim Aralığı = En büyük - En küçük',
      'Medyan = (n+1)/2. sıradaki değer (n tek ise)'
    ],
    examples: [
      'Veri seti: 3, 5, 7, 8, 8, 10',
      'Ortalama: (3+5+7+8+8+10) ÷ 6 = 6.83',
      'Medyan: (7+8) ÷ 2 = 7.5',
      'Mod: 8 (en sık tekrar eden)',
      'Değişim Aralığı: 10 - 3 = 7'
    ],
    difficulty: 'easy',
    estimatedReadTime: 7,
    tags: ['veri', 'istatistik', 'ortalama', 'medyan', 'mod'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-005',
    title: 'Olasılık',
    subject: 'matematik',
    topic: 'Olasılık',
    content: `# Olasılık

## Olasılık Nedir?
Bir olayın gerçekleşme şansının sayısal ifadesidir.
**Değer Aralığı**: 0 ≤ P(A) ≤ 1

## Temel Kavramlar
### Örnek Uzay (Ω)
Bir deneyde çıkabilecek tüm sonuçların kümesidir.
**Örnek**: Zar atma → Ω = {1, 2, 3, 4, 5, 6}

### Olay
Örnek uzayın bir alt kümesidir.
**Örnek**: Çift sayı gelme olayı → A = {2, 4, 6}

### Kesin Olay
Olasılığı 1 olan olay. Mutlaka gerçekleşir.

### İmkansız Olay
Olasılığı 0 olan olay. Asla gerçekleşmez.

## Olasılık Hesaplama
**Klasik Olasılık**: P(A) = Olay A'nın eleman sayısı / Örnek uzayın eleman sayısı

## Olasılık Kuralları
- P(A') = 1 - P(A) (Tümler olay)
- P(A ∪ B) = P(A) + P(B) - P(A ∩ B) (Birleşim)
- Ayrık olaylar için: P(A ∪ B) = P(A) + P(B)`,
    keyPoints: [
      'Olasılık 0 ile 1 arasında bir değerdir',
      'Örnek uzay, tüm mümkün sonuçları içerir',
      'Kesin olayın olasılığı 1, imkansız olayın 0\'dır',
      'Tümler olayın olasılığı 1 - P(A)\'dır',
      'Ayrık olayların birleşim olasılığı toplama eşittir'
    ],
    formulas: [
      'P(A) = n(A) / n(Ω)',
      'P(A\') = 1 - P(A)',
      'P(A ∪ B) = P(A) + P(B) - P(A ∩ B)',
      '0 ≤ P(A) ≤ 1'
    ],
    examples: [
      'Zar atma: P(çift) = 3/6 = 1/2',
      'Yazı-tura: P(yazı) = 1/2',
      'Kart çekme: P(kupa) = 13/52 = 1/4',
      'P(çift değil) = 1 - P(çift) = 1/2',
      'İki zarın toplamı 7: P = 6/36 = 1/6'
    ],
    difficulty: 'medium',
    estimatedReadTime: 6,
    tags: ['olasılık', 'örnek uzay', 'olay', 'kesin olay', 'imkansız olay'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-006',
    title: 'Cebirsel İfadeler',
    subject: 'matematik',
    topic: 'Cebir',
    content: `# Cebirsel İfadeler

## Cebirsel İfade Nedir?
Sayılar, harfler (değişkenler) ve işlem sembolleriyle oluşturulan ifadelerdir.
**Örnekler**: 3x + 5, 2a - 7b, x² + 4x - 12

## Temel Kavramlar
### Değişken (Bilinmeyen)
Değeri değişebilen, harf ile gösterilen niceliktir.
**Örnekler**: x, y, a, b

### Katsayı
Değişkenin önündeki sayıdır.
**Örnek**: 5x'te 5 katsayısıdır.

### Sabit Terim
İçinde değişken bulunmayan terimdir.
**Örnek**: 3x + 7'de 7 sabit terimdir.

## Cebirsel İfadelerde İşlemler
### Toplama ve Çıkarma
Benzer terimler toplanır veya çıkarılır.
**Benzer terimler**: Aynı değişkene sahip terimler

### Çarpma
- Sayı ile terim: 3 × 2x = 6x
- Terim ile terim: 2x × 3y = 6xy

### Çarpanlara Ayırma
Ortak çarpanı paranteze alma işlemidir.
**Örnek**: 6x + 9 = 3(2x + 3)`,
    keyPoints: [
      'Cebirsel ifadeler harfler ve sayılardan oluşur',
      'Benzer terimler toplanabilir ve çıkarılabilir',
      'Katsayı, değişkenin önündeki sayıdır',
      'Çarpanlara ayırmada ortak çarpan bulunur',
      'Değişkenlerin derecesi üs sayısına eşittir'
    ],
    formulas: [
      'ax + bx = (a + b)x',
      'ax - bx = (a - b)x',
      'a × bx = abx',
      'ax + by (benzer olmayan terimler toplanamaz)'
    ],
    examples: [
      '3x + 5x = 8x',
      '7y - 2y = 5y',
      '4a × 3b = 12ab',
      '8x + 12 = 4(2x + 3)',
      '2x² + 3x + 1 (3 terimli ifade)'
    ],
    difficulty: 'easy',
    estimatedReadTime: 5,
    tags: ['cebir', 'değişken', 'katsayı', 'terim', 'çarpanlara ayırma'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-007',
    title: 'Doğrusal Denklemler',
    subject: 'matematik',
    topic: 'Denklemler',
    content: `# Doğrusal Denklemler

## Denklem Nedir?
İçinde bilinmeyen bulunan ve eşitlik işareti olan matematiksel ifadelerdir.
**Örnek**: 2x + 5 = 11

## Birinci Dereceden Bir Bilinmeyenli Denklemler
En yüksek derecesi 1 olan, bir bilinmeyenli denklemlerdir.
**Genel Şekli**: ax + b = 0 (a ≠ 0)

## Denklem Çözme Kuralları
1. **Eşitliğin iki tarafına aynı sayı eklenebilir**
2. **Eşitliğin iki tarafından aynı sayı çıkarılabilir**
3. **Eşitliğin iki tarafı aynı sayıyla çarpılabilir**
4. **Eşitliğin iki tarafı aynı sayıya bölünebilir** (0 hariç)

## Çözüm Adımları
1. Parantezleri aç
2. Benzer terimleri topla
3. Bilinmeyenleri bir tarafa, sayıları diğer tarafa geçir
4. Bilinmeyeni yalnız bırak

## Denklem Türleri
- **Özdeş Denklem**: Her değer için doğru (∞ çözüm)
- **Çelişkili Denklem**: Hiçbir değer için doğru değil (çözüm yok)
- **Koşullu Denklem**: Belirli değer(ler) için doğru`,
    keyPoints: [
      'Denklem, bilinmeyen içeren eşitliktir',
      'Çözüm, denklemi sağlayan bilinmeyen değeridir',
      'Eşitliğin iki tarafına aynı işlem uygulanabilir',
      'Bilinmeyenler bir tarafa, sayılar diğer tarafa geçirilir',
      'Özdeş denklemin sonsuz, çelişkilinin çözümü yoktur'
    ],
    formulas: [
      'ax + b = 0 → x = -b/a',
      'ax + b = cx + d → x = (d-b)/(a-c)',
      'Kontrol: Bulunan değeri yerine koy'
    ],
    examples: [
      '2x + 3 = 11 → 2x = 8 → x = 4',
      '5x - 7 = 3x + 1 → 2x = 8 → x = 4',
      '3(x + 2) = 15 → 3x + 6 = 15 → x = 3',
      '4x = 4x + 1 (çelişkili, çözüm yok)',
      '2x + 5 = 2x + 5 (özdeş, sonsuz çözüm)'
    ],
    difficulty: 'medium',
    estimatedReadTime: 7,
    tags: ['denklem', 'bilinmeyen', 'çözüm', 'eşitlik', 'doğrusal'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-008',
    title: 'Eşitsizlikler',
    subject: 'matematik',
    topic: 'Eşitsizlikler',
    content: `# Eşitsizlikler

## Eşitsizlik Nedir?
İki matematiksel ifade arasındaki büyüklük-küçüklük ilişkisini gösteren ifadelerdir.
**Semboller**: <, >, ≤, ≥

## Eşitsizlik Türleri
- **a < b**: a, b'den küçüktür
- **a > b**: a, b'den büyüktür  
- **a ≤ b**: a, b'den küçük veya eşittir
- **a ≥ b**: a, b'den büyük veya eşittir

## Eşitsizlik Özellikleri
### Toplama-Çıkarma
Eşitsizliğin iki tarafına aynı sayı eklenebilir veya çıkarılabilir.
**a < b ⟹ a + c < b + c**

### Pozitif Sayıyla Çarpma-Bölme
Eşitsizlik yönü değişmez.
**a < b ve c > 0 ⟹ ac < bc**

### Negatif Sayıyla Çarpma-Bölme
Eşitsizlik yönü değişir.
**a < b ve c < 0 ⟹ ac > bc**

## Birinci Dereceden Eşitsizlikler
ax + b < 0, ax + b > 0, ax + b ≤ 0, ax + b ≥ 0 şeklindeki eşitsizlikler.

## Çözüm Kümesi Gösterimi
- Sayı doğrusu üzerinde gösterim
- Aralık gösterimi: [a, b), (a, b], (a, b), [a, b]`,
    keyPoints: [
      'Eşitsizlikler büyüklük-küçüklük ilişkisini gösterir',
      'Pozitif sayıyla çarpımda yön değişmez',
      'Negatif sayıyla çarpımda yön değişir',
      'Çözüm kümesi genellikle aralık şeklindedir',
      'Sayı doğrusu üzerinde görsel olarak gösterilebilir'
    ],
    formulas: [
      'a < b ⟹ a + c < b + c',
      'a < b ve c > 0 ⟹ ac < bc',
      'a < b ve c < 0 ⟹ ac > bc',
      'ax + b < 0 ⟹ x < -b/a (a > 0)'
    ],
    examples: [
      '2x + 3 < 11 → 2x < 8 → x < 4',
      '-3x + 6 ≥ 0 → -3x ≥ -6 → x ≤ 2',
      '5 ≤ 2x + 1 < 9 → 2 ≤ x < 4',
      'x ∈ (-∞, 4) (x < 4)',
      'x ∈ [2, 5] (2 ≤ x ≤ 5)'
    ],
    difficulty: 'medium',
    estimatedReadTime: 6,
    tags: ['eşitsizlik', 'büyük', 'küçük', 'aralık', 'çözüm kümesi'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-009',
    title: 'Üçgenler',
    subject: 'matematik',
    topic: 'Geometri',
    content: `# Üçgenler

## Üçgen Nedir?
Üç kenarı ve üç açısı olan kapalı geometrik şekildir.
**Açılar Toplamı**: Her üçgende iç açılar toplamı 180°'dir.

## Üçgen Türleri
### Kenar Uzunluklarına Göre
- **Eşkenar Üçgen**: Üç kenarı da eşit (60°-60°-60°)
- **İkizkenar Üçgen**: İki kenarı eşit
- **Çeşitkenar Üçgen**: Üç kenarı da farklı

### Açılarına Göre
- **Dar Açılı Üçgen**: Tüm açıları 90°'den küçük
- **Dik Açılı Üçgen**: Bir açısı 90°
- **Geniş Açılı Üçgen**: Bir açısı 90°'den büyük

## Önemli Teoremler
### Pisagor Teoremi
Dik üçgenlerde: a² + b² = c² (c hipotenüs)

### Üçgen Eşitsizliği
Herhangi iki kenarın toplamı, üçüncü kenardan büyüktır.
|a - b| < c < a + b

## Üçgen Çevresi ve Alanı
- **Çevre**: Tüm kenarların toplamı
- **Alan**: (taban × yükseklik) ÷ 2`,
    keyPoints: [
      'Üçgende iç açılar toplamı 180°\'dir',
      'Eşkenar üçgende tüm açılar 60°\'dir',
      'Dik üçgenlerde Pisagor teoremi geçerlidir',
      'Üçgen eşitsizliği her üçgen için geçerlidir',
      'Alan formülü: (taban × yükseklik) ÷ 2'
    ],
    formulas: [
      'İç açılar toplamı = 180°',
      'Pisagor: a² + b² = c²',
      'Çevre = a + b + c',
      'Alan = (a × h) ÷ 2',
      'Üçgen eşitsizliği: |a-b| < c < a+b'
    ],
    examples: [
      'Açıları 40°, 60°, 80° olan üçgen',
      'Kenarları 3, 4, 5 birimlik dik üçgen',
      'Eşkenar üçgen: 60°-60°-60°',
      'Alan: taban 6, yükseklik 4 → A = 12',
      'Üçgen oluşumu: 3, 4, 8 oluşturmaz (3+4 < 8)'
    ],
    difficulty: 'easy',
    estimatedReadTime: 6,
    tags: ['üçgen', 'açı', 'pisagor', 'alan', 'çevre'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-010',
    title: 'Eşlik ve Benzerlik',
    subject: 'matematik',
    topic: 'Geometri',
    content: `# Eşlik ve Benzerlik

## Eş Şekiller
Üst üste konulduğunda tamamen örtüşen şekillerdir.
**Özellikler**:
- Karşılıklı kenarları eşit
- Karşılıklı açıları eşit  
- Alanları eşit
- Çevreleri eşit

## Benzer Şekiller
Aynı şekle sahip ancak boyutları farklı olan şekillerdir.
**Özellikler**:
- Karşılıklı açıları eşit
- Karşılıklı kenarları orantılı
- Alan oranı = (kenar oranı)²
- Çevre oranı = kenar oranı

## Benzerlik Oranı
Benzer şekillerin karşılıklı kenarlarının oranına **benzerlik oranı** denir.
**k = a'/a = b'/b = c'/c**

## Üçgenlerde Benzerlik
### AA Benzerliği
İki açısı eşit olan üçgenler benzerdir.

### SAS Benzerliği  
İki kenarı orantılı ve aralarındaki açı eşit olan üçgenler benzerdir.

### SSS Benzerliği
Üç kenarı orantılı olan üçgenler benzerdir.

## Benzer Şekillerde Oranlar
- Kenar oranı = k
- Çevre oranı = k  
- Alan oranı = k²
- Hacim oranı = k³ (üç boyutlu şekillerde)`,
    keyPoints: [
      'Eş şekiller tamamen aynı boyutlardadır',
      'Benzer şekillerin açıları eşit, kenarları orantılıdır',
      'Benzerlik oranı tüm kenar çiftleri için aynıdır',
      'Alan oranı, kenar oranının karesidir',
      'Üçgenlerde AA, SAS, SSS benzerlik kriterleri vardır'
    ],
    formulas: [
      'Benzerlik oranı: k = a\'/a',
      'Çevre oranı = k',
      'Alan oranı = k²',
      'Hacim oranı = k³'
    ],
    examples: [
      'Kenar oranı 1:2 → Alan oranı 1:4',
      'Açıları 30°-60°-90° olan üçgenler benzer',
      'Kenarları 3-4-5 ve 6-8-10 olan üçgenler benzer',
      'Benzerlik oranı 3:5 → Çevre oranı 3:5',
      'k = 2 ise yeni alan 4 katı olur'
    ],
    difficulty: 'medium',
    estimatedReadTime: 7,
    tags: ['eşlik', 'benzerlik', 'oran', 'üçgen', 'geometri'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-011',
    title: 'Dönüşüm Geometrisi',
    subject: 'matematik',
    topic: 'Geometri',
    content: `# Dönüşüm Geometrisi

## Dönüşüm Türleri

### Öteleme (Paralel Öteleme)
Bir şeklin belli bir yön ve mesafe ile hareket ettirilmesidir.
**Özellikler**:
- Şekil ve boyutlar değişmez
- Yön ve uzaklık sabittir
- Paralel doğrular paralel kalır

### Yansıma (Simetri)
Bir şeklin eksen etrafında ayna görüntüsünün alınmasıdır.
**Özellikler**:
- Şekil ve boyutlar değişmez
- Simetri eksenine eşit uzaklıktaki noktalar birbirine karşılık gelir
- Açılar korunur

### Döndürme (Rotasyon)
Bir şeklin sabit bir nokta etrafında belli açıyla çevrilmesidir.
**Özellikler**:
- Şekil ve boyutlar değişmez
- Döndürme merkezi sabit kalır
- Açı miktarı ve yönü önemlidir

### Homotheti (Benzerlik Dönüşümü)
Bir şeklin sabit bir merkezden belli oranda büyütülmesi veya küçültülmesidir.
**Özellikler**:
- Şekil aynı kalır, boyut değişir
- Açılar korunur
- Orantı sabittir

## Dönüşüm Çeşitleri
- **Rigid Motion**: Şekli koruyan dönüşümler (öteleme, yansıma, döndürme)
- **Similarity**: Şekli koruyan ama boyutu değiştiren (homotheti)`,
    keyPoints: [
      'Ötelemede şekil sabit yön ve mesafede hareket eder',
      'Yansımada şekil simetri ekseninin diğer tarafına geçer',
      'Döndürmede şekil sabit nokta etrafında döner',
      'Homothetide şekil aynı kalır, boyut değişir',
      'Rigid motions şekil ve boyutu korur'
    ],
    formulas: [
      'Öteleme: (x, y) → (x + a, y + b)',
      'Yansıma (y-eksenine): (x, y) → (-x, y)',
      'Döndürme (90° saat yönü): (x, y) → (y, -x)',
      'Homotheti: (x, y) → (kx, ky)'
    ],
    examples: [
      'A(2,3) noktasının (1,2) ötelemesi → A\'(3,5)',
      'B(4,1) noktasının y-eksenine yansıması → B\'(-4,1)',
      'C(3,2) noktasının orijin etrafında 90° dönmesi → C\'(2,-3)',
      'D(2,4) noktasının 2 kat homothetisi → D\'(4,8)',
      'Üçgenin tüm köşelerini aynı dönüşüme uygula'
    ],
    difficulty: 'hard',
    estimatedReadTime: 8,
    tags: ['dönüşüm', 'öteleme', 'yansıma', 'döndürme', 'homotheti'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  },
  {
    id: 'mt-012',
    title: 'Geometrik Cisimler',
    subject: 'matematik',
    topic: 'Geometri',
    content: `# Geometrik Cisimler

## Prizmalar
Tabanları paralel ve eş olan çok yüzlülerdir.
**Dik Prizmanın Özellikleri**:
- Yan yüzleri dikdörtgendir
- Hacim = Taban Alanı × Yükseklik
- Yüzey Alanı = 2 × Taban Alanı + Yanal Alan

### Küp
Tüm kenarları eşit olan özel prizmadır.
- Hacim = a³
- Yüzey Alanı = 6a²

### Dikdörtgenler Prizması
Tabanı dikdörtgen olan prizmadır.
- Hacim = a × b × c
- Yüzey Alanı = 2(ab + ac + bc)

## Piramitler
Tabanı çokgen, yan yüzleri üçgen olan cisimlerdir.
**Özellikler**:
- Hacim = (Taban Alanı × Yükseklik) ÷ 3
- Yanal Alan = (Taban Çevresi × Yanal Yükseklik) ÷ 2

## Silindir
Tabanları daire olan prizmadır.
- Hacim = π × r² × h
- Yüzey Alanı = 2πr² + 2πrh

## Koni
Tabanı daire olan piramittir.
- Hacim = (π × r² × h) ÷ 3
- Yanal Alan = π × r × l (l: ana doğru)

## Küre
Merkezden eşit uzaklıktaki noktalardan oluşan cisimdir.
- Hacim = (4πr³) ÷ 3
- Yüzey Alanı = 4πr²`,
    keyPoints: [
      'Prizma hacmi: Taban alanı × Yükseklik',
      'Piramit hacmi: (Taban alanı × Yükseklik) ÷ 3',
      'Silindir π\'li formüller kullanır',
      'Koni, piramidin daire tabanlı halidir',
      'Küre hacmi (4πr³) ÷ 3, yüzey alanı 4πr²'
    ],
    formulas: [
      'Küp hacmi: V = a³',
      'Dikdörtgenler prizması: V = a × b × c',
      'Silindir hacmi: V = πr²h',
      'Koni hacmi: V = (πr²h) ÷ 3',
      'Küre hacmi: V = (4πr³) ÷ 3'
    ],
    examples: [
      'Küp (a=3): V = 27, A = 54',
      'Silindir (r=2, h=5): V = 20π',
      'Koni (r=3, h=4): V = 12π',
      'Küre (r=3): V = 36π, A = 36π',
      'Dikdörtgenler prizması (2×3×4): V = 24'
    ],
    difficulty: 'medium',
    estimatedReadTime: 8,
    tags: ['cisim', 'hacim', 'yüzey alanı', 'prizma', 'piramit', 'silindir'],
    color: '#3B82F6',
    isPublished: true,
    viewCount: 0,
    likeCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin'
  }
];